<template>
    <div class="product-type-content">

        <div class="add-button">
            <el-button type="primary" @click="onAdd">新增一级类别</el-button>
        </div>

        <div class="main">
            <div class="title">产品类别管理</div>

            <div class="tree">
                <el-tree style="max-width: 600px" :allow-drop="allowDrop" :allow-drag="allowDrag" :data="treeData"
                    draggable :props="{ label: 'name', children: 'children' }" default-expand-all node-key="id"
                    @node-drag-start="handleDragStart" @node-drag-enter="handleDragEnter"
                    @node-drag-leave="handleDragLeave" @node-drag-over="handleDragOver" @node-drag-end="handleDragEnd"
                    @node-drop="handleDrop" :expand-on-click-node="false">

                    <template #default="{ node, data }">
                        <div class="custom-tree-node">
                            <span>{{ node.label }}</span>
                            <div>
                                <el-button type="primary" link v-if="data.level === 1" @click="addChildNode(node,data)">
                                    添加子类别
                                </el-button>
                                <el-button style="margin-left: 4px" type="success" link @click="modifyNode(node,data)">
                                    修改
                                </el-button>
                                <el-button style="margin-left: 4px" type="danger" link @click="deleteNode(node,data)">
                                    删除
                                </el-button>
                            </div>
                        </div>
                    </template>
                </el-tree>
            </div>

            <el-button type="primary" plain @click="save">保存</el-button>
        </div>

        <div class="tips">
            <span class="red">*</span>
            <span>
                使用说明：本模块用于管理产品的类别信息，用户可以通过点击[新增一级类别]按钮增加一个一级产品标题，在[产品类别管理]下的树状结构大纲展现的是当前产品的所有类别信息，用户可以通过点击每个标题右侧的操作按钮进行操作，例如[添加子类别]可以为当前一级类别添加子类别、[修改]可以修改当前类别名称、[删除]可以直接删除当前类别（如果存在子类别的将一并删除），用户还可以通过[鼠标拖拽]行为改变这些类别标题的顺序、级别。执行完所有的操作后都需要点击最下方的[保存]按钮才会最终生效。
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { DragEvents } from 'element-plus/es/components/tree/src/model/useDragNode'
import {
    ElMessageBox,
    type AllowDropType,
    type NodeDropType,
    type RenderContentContext,
} from 'element-plus'
import { ProductTypes } from '@/typeDefine/EntityType'
import { onMounted, ref, Ref } from 'vue'
import { Action } from '@/typeDefine/CommonTypes'
import { BaseResponse, GetProductTypesResponse } from '@/typeDefine/HttpResponseType'
import { Api } from '@/http/Api'
import { UtilMethods } from '@/util/UtilMethods'
import { ResponseCode } from '@/http/ResponseCode'

type Node = RenderContentContext['node']

const treeData: Ref<ProductTypes[]> = ref([])

const maxId: Ref<number> = ref(0)

onMounted(async () => {
    await requestProductTypes()

    maxId.value = findMaxId(treeData.value) + 1
})

//查找treeData中id最大的值并记录
const findMaxId: Action<[ProductTypes[]], number> = (data) => {
    let maxId = -Infinity

    const traverse = (nodes: ProductTypes[]) => {
        for (const node of nodes) {
            if (node.id > maxId) {
                maxId = node.id
            }

            if (Array.isArray(node.children) && node.children.length > 0) {
                traverse(node.children)
            }
        }
    }

    traverse(data)

    return maxId === -Infinity ? 0 : maxId
}  

//请求树状类别
const requestProductTypes: Action = async () => {
    const response: GetProductTypesResponse = await Api.instance.getProductTypes()

    UtilMethods.showMessageToast(response, false)

    if (response.code === ResponseCode.OK) {
        treeData.value = response.data.treeType
    }
}

//递归查找子节点
const findNode: Action<[ProductTypes[], number], ProductTypes | null> = (data, targetId) => {
    for (const node of data) {
        if (node.id === targetId) {
            return node
        }

        if (node.children && node.children.length > 0) {
            const found = findNode(node.children, targetId)
            if (found) return found
        }
    }

    return null
}

//处理更新后的树状结构列表
const treeUpdateHandler: Action<[ProductTypes[]]> = (tree: ProductTypes[]) => {
    const updateRecursive = (
        nodes: ProductTypes[],
        parentId: number,
        level: number
    ): void => {
        nodes.forEach((node, index) => {
            node.parentId = parentId
            node.level = level
            node.sort = index

            if (Array.isArray(node.children) && node.children.length > 0) {
                updateRecursive(node.children, node.id, level + 1)
            }
        })
    }

    updateRecursive(tree, 0, 1) // 顶级节点 parentId 为 0，level 为 1
}

//打开Prompt填写内容
const openPrompt: Action<[string?], Promise<string | null>> = async (initValue) => {
    return new Promise<string | null>((resolve, reject) => {
        ElMessageBox.prompt('请输入标题名称', '提示', {
            confirmButtonText: "确认",
            inputValue: initValue,
            inputValidator(value) {
                if (value?.trim()?.length <= 0 || value?.trim()?.length > 255) {
                    return '类型标题长度应在1-255个字符之间'
                }

                return true
            },
        }).then(({ value }) => {
            resolve(value)
        }).catch(() => { 
            resolve(null)
        })
    })
}

//添加一级类别
const onAdd: Action = async () => {
    const name: string | null = await openPrompt()

    if (!name) {
        return
    }

    treeData.value.push({
        id: maxId.value++,
        name,
        parentId: 0,
        level: 1,
        sort: 0,
        children: [],
        createTime: new Date()
    })
}

//添加子类项
const addChildNode: Action<[Node,ProductTypes]> = async (node,data) => {
    const name: string | null = await openPrompt()
    
    if(!name) {
        return
    }

    const item: ProductTypes | null = findNode(treeData.value,data.id)
    if(!item) {
        return
    }

    item.children.push({
        id: maxId.value++,
        name,
        parentId: data.id,
        level: data.level + 1,
        sort: item.children.length,
        children: [],
        createTime: new Date()
    })
}

//修改节点名称
const modifyNode: Action<[Node,ProductTypes]> = async (node,data) => {
    const name: string | null = await openPrompt(data.name)

    if (!name) {
        return
    }

    const item: ProductTypes | null = findNode(treeData.value,data.id)
    if (!item) {
        return
    }

    item.name = name

}

//删除节点
const deleteNode: Action<[Node,ProductTypes]> = async (node,data) => {
    
    const deleteNodeByRecursion = (tree: ProductTypes[], targetId: number) => {
        for (let i = tree.length - 1; i >= 0; --i) {
            const node = tree[i]

            if (node.id === targetId) {
                tree.splice(i, 1)
                break
            }

            if (node.children && node.children.length > 0) {
                deleteNodeByRecursion(node.children, targetId)
            }
        }
    }

    deleteNodeByRecursion(treeData.value,data.id)
    treeUpdateHandler(treeData.value)
}

//保存所有结果
const save: Action = async () => {
    const response: BaseResponse = await Api.instance.saveProductType({
        productType: treeData.value
    })

    UtilMethods.showMessageToast(response)
}

const handleDragStart = (node: Node, ev: DragEvents) => {

}
const handleDragEnter = (
    draggingNode: Node,
    dropNode: Node,
    ev: DragEvents
) => {

}
const handleDragLeave = (
    draggingNode: Node,
    dropNode: Node,
    ev: DragEvents
) => {

}
const handleDragOver = (draggingNode: Node, dropNode: Node, ev: DragEvents) => {

}
const handleDragEnd = (
    draggingNode: Node,
    dropNode: Node,
    dropType: NodeDropType,
    ev: DragEvents
) => {
    treeUpdateHandler(treeData.value)
}
const handleDrop = (
    draggingNode: Node,
    dropNode: Node,
    dropType: NodeDropType,
    ev: DragEvents
) => {

}
const allowDrop: Action<[Node, Node, AllowDropType], boolean> = (draggingNode: Node, dropNode: Node, type: AllowDropType) => {

    if (dropNode.data.level === 2 && type === 'inner') {
        return false
    }

    if (Array.isArray(draggingNode.data.children) && draggingNode.data.children.length > 0 && (type === 'inner' || dropNode.data.level === 2)) {
        return false
    }

    return true
}
const allowDrag = (draggingNode: Node) => {
    return true
}

</script>

<style lang="css" scoped>
.product-type-content .add-button {
    margin: 20px 0;
}
.product-type-content .main .title {
    font-size: 20px;
    font-weight: bold;
}

.product-type-content .main .custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding-right: 8px;
}

.product-type-content .main .tree {
    margin: 30px 0;
}

.product-type-content .tips {
    font-size: 14px;
    margin-top: 20px;
    line-height: 30px;
}
</style>